/**
 * src/api/mfaApi.js
 *
 * Pre-authentication HTTP helpers for the MFA flow.
 *
 * These calls carry state in short-lived challenge/auth tokens (JWT body)
 * rather than a session Bearer token, so they bypass the standard
 * azureGet/azurePost session-authenticated path.
 */

import { getFunctionUrl } from './functionUrl.js';
import { getStepUpToken, requestStepUp, isStepUpRequired, STEP_UP_CANCELLED } from './stepUp.js';

// `baseUrl` (optional) targets a specific environment's Function App
// (e.g. the test FA for a beta tenant during central login). When omitted,
// the build-time getFunctionUrl (current environment) is used.
async function post(functionName, body, baseUrl) {
  const url = baseUrl ? `${baseUrl}/api/${functionName}` : getFunctionUrl(functionName);
  let res;
  try {
    res = await fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
    });
  } catch (_e) {
    return { error: 'Network error. Please try again.' };
  }
  const data = await res.json().catch(() => ({}));
  if (!res.ok) return { error: data.error || `${functionName} failed (${res.status})` };
  return data;
}

/**
 * Call mfa-verify with a given action.
 *
 * Actions and required body fields:
 *   'webauthn-options' — { challengeToken }
 *   'webauthn-verify'  — { authToken, authenticationResponse, origin }
 *   'totp'             — { challengeToken, code }
 *
 * Does NOT attach a session JWT — authentication is via challengeToken or
 * authToken in the body (both are short-lived, single-purpose JWTs).
 */
export function callMfaVerify(action, body, baseUrl) {
  return post('mfa-verify', { action, ...body }, baseUrl);
}

/**
 * Call mfa-setup with a given action.
 *
 * Actions and required body fields:
 *   'webauthn-options' — { challengeToken }
 *   'webauthn-verify'  — { setupToken, registrationResponse, origin }
 *   'totp-generate'    — { challengeToken }
 *   'totp-confirm'     — { setupToken, code }
 *
 * Does NOT attach a session JWT — authentication is via challengeToken
 * or setupToken in the body.
 */
export function callMfaSetup(action, body, baseUrl) {
  return post('mfa-setup', { action, ...body }, baseUrl);
}

/**
 * Request an MFA recovery email.
 * POST mfa-recovery-request — { tenantId, username }
 * Returns { status: 'sent' } always (no information leakage).
 */
export function callMfaRecoveryRequest(tenantId, username, baseUrl) {
  return post('mfa-recovery-request', { tenantId, username }, baseUrl);
}

/**
 * Verify the recovery token from the email link.
 * POST mfa-recovery-verify — { token }
 * Returns { status: 'mfa-setup-required', challengeToken, reason: 'device-recovery' }
 * so the existing mfa-setup flow takes over.
 */
export function callMfaRecoveryVerify(token, baseUrl) {
  return post('mfa-recovery-verify', { token }, baseUrl);
}

/**
 * Begin self-service MFA enrolment for a logged-in, un-enrolled user.
 * POST mfa-enroll — { password } with a session JWT in the Authorization header.
 * Returns { challengeToken } on success, which is passed to MfaSetupModal
 * (mfa-setup) to register the new factor.
 */
export async function callMfaEnrollStart(password, sessionToken) {
  const url = getFunctionUrl('mfa-enroll');
  const headers = { 'Content-Type': 'application/json' };
  if (sessionToken) headers['Authorization'] = `Bearer ${sessionToken}`;
  let res;
  try {
    res = await fetch(url, {
      method: 'POST',
      headers,
      body: JSON.stringify({ password }),
    });
  } catch (e) {
    return { error: 'Network error. Please try again.' };
  }
  const data = await res.json().catch(() => ({}));
  if (!res.ok) return { error: data.error || `mfa-enroll failed (${res.status})` };
  return data;
}

/**
 * Request a password-reset magic link for a user whose temp password has expired.
 * POST password-reset-magic-link — { tenantId, username }
 * Returns { success: true } always (no information leakage).
 */
export function callPasswordResetMagicLink(tenantId, username, baseUrl) {
  return post('password-reset-magic-link', { tenantId, username }, baseUrl);
}

/**
 * Exchange a password-reset magic link token for an mfa-challenge token.
 * POST password-reset-verify — { tenantId, token }
 * Returns { status: 'mfa-setup-required', reason: 'password-reset', challengeToken, username }
 * so the existing mfa-setup flow takes over.
 */
export function callPasswordResetVerify(tenantId, token, baseUrl) {
  return post('password-reset-verify', { tenantId, token }, baseUrl);
}

/**
 * Call mfa-change with a given action.
 * Requires a valid session JWT (the user must already be logged in).
 *
 * Every action needs a §16 step-up token: on `403 { stepUp: 'required' }` this
 * opens the shared re-verify dialog and retries once.
 *
 * Actions and required body fields:
 *   'verify-current'   — {} (the step-up is the proof of the current factor)
 *   'webauthn-options' — { changeToken } (registration options for the new passkey)
 *   'webauthn-verify'  — { setupToken, registrationResponse, origin }
 *   'totp-generate'    — { changeToken }
 *   'totp-confirm'     — { setupToken, code }
 */
export async function callMfaChange(action, body, sessionToken) {
  const url = getFunctionUrl('mfa-change');
  const send = async () => {
    const headers = { 'Content-Type': 'application/json' };
    if (sessionToken) headers['Authorization'] = `Bearer ${sessionToken}`;
    // §16: every mfa-change action needs a step-up token (./stepUp.js).
    const stepUp = getStepUpToken();
    if (stepUp) headers['X-Step-Up-Token'] = stepUp;
    return fetch(url, { method: 'POST', headers, body: JSON.stringify({ action, ...body }) });
  };
  let res;
  try {
    res = await send();
    // The gate refused: run the shared re-verify dialog, then retry once.
    if (await isStepUpRequired(res)) {
      if (!(await requestStepUp())) return { error: STEP_UP_CANCELLED, stepUpCancelled: true };
      res = await send();
    }
  } catch (_e) {
    return { error: 'Network error. Please try again.' };
  }
  const data = await res.json().catch(() => ({}));
  if (!res.ok) return { error: data.error || `mfa-change failed (${res.status})` };
  return data;
}

/**
 * Call step-up-verify (§16) with the session JWT.
 *
 *   'begin'  — {} → { method: 'totp', totpTag } | { method: 'webauthn', authenticationOptions, authToken }
 *   'verify' — { code } | { authToken, authenticationResponse, origin } → { stepUpToken, expiresAt }
 *
 * On failure returns { error, stepUp? } — stepUp is 'enrol' (no factor yet: run
 * mfa-enroll + mfa-setup first) or 'recovery' (factor unusable).
 */
export function callStepUpVerify(action, body, sessionToken) {
  return sessionPost('step-up-verify', { action, ...body }, sessionToken);
}

/**
 * POST with a session JWT, keeping any `stepUp` routing hint ('enrol' /
 * 'recovery' / 'required') from an error response.
 * @param {string} functionName
 * @param {object} body
 * @param {string | null | undefined} sessionToken
 */
async function sessionPost(functionName, body, sessionToken) {
  const url = getFunctionUrl(functionName);
  const headers = { 'Content-Type': 'application/json' };
  if (sessionToken) headers['Authorization'] = `Bearer ${sessionToken}`;
  let res;
  try {
    res = await fetch(url, {
      method: 'POST',
      headers,
      body: JSON.stringify(body),
    });
  } catch (_e) {
    return { error: 'Network error. Please try again.' };
  }
  const data = await res.json().catch(() => ({}));
  if (!res.ok) {
    return {
      error: data.error || `${functionName} failed (${res.status})`,
      ...(typeof data.stepUp === 'string' ? { stepUp: data.stepUp } : {}),
    };
  }
  return data;
}

/**
 * Super-admin factor enrolment start (FDNA-02b). Proof is the account password
 * or a fresh Google sign-in credential. Returns { challengeToken } | { error }.
 * @param {{ action?: 'google-nonce', password?: string, googleCredential?: string, nonceToken?: string }} proof
 * @param {string | null | undefined} sessionToken
 */
export function callSuperAdminMfaEnrollStart(proof, sessionToken) {
  return sessionPost('superadmin-mfa-enroll', proof, sessionToken);
}

/**
 * Super-admin factor registration (FDNA-02b) — same actions as callMfaSetup,
 * with the super-admin session JWT attached. Completion returns { status: 'ok' }
 * and no new session.
 * @param {string} action
 * @param {object} body
 * @param {string | null | undefined} sessionToken
 */
export function callSuperAdminMfaSetup(action, body, sessionToken) {
  return sessionPost('superadmin-mfa-setup', { action, ...body }, sessionToken);
}

/**
 * Second step of super-admin sign-in (FDNA-02b-2). No session yet: the
 * sa-login-challenge token from superadmin-login / superadmin-google-login
 * travels in the body.
 *   'begin'  — { challengeToken } → { method, totpTag } | { method: 'webauthn', authenticationOptions, authToken }
 *   'verify' — { challengeToken, code } | { challengeToken, authToken, authenticationResponse, origin }
 *              → { token, user }
 * @param {'begin' | 'verify'} action
 * @param {object} body
 */
export function callSuperAdminLoginVerify(action, body) {
  return post('superadmin-login-verify', { action, ...body });
}
